import { Item, Section } from '@react-stately/collections'
import { Select } from 'components/Select/Select'
import { FunctionComponent, Key, SVGProps } from 'react'
import { ActionSpec, ActionType } from 'stores/Workflows/Types'
import { ReadReceipt } from 'almanac-ui/icons/16/ReadReceipt'
import { Approval } from 'almanac-ui/icons/16/Approval'
import { Review } from 'almanac-ui/icons/16/Review'

import { ReactComponent as DocIcon } from 'assets/icons/document16.svg'
import { ReactComponent as ShareIcon } from 'assets/icons/share16.svg'
import { ReactComponent as ExportIcon } from 'assets/icons/export16.svg'
import { ReactComponent as MoveIcon } from 'assets/icons/moveDoc16.svg'
import { ConfigureReviewAutomationModal } from 'components/ReviewAutomation/ConfigureReviewAutomationModal'
import { CHILD_STATE_NAMES, State } from 'stores/Workflows/State'
import { ReviewType } from 'components/ReviewsWizard/types'
import { Button } from 'components/Button'
import { ReactComponent as SettingsIcon } from 'assets/icons/settings16.svg'
import { ReactComponent as ChevronRight } from 'assets/icons/chevronRight16.svg'
import Styles from './WorkflowBuilderItem.module.scss'
import { observer } from 'mobx-react-lite'
import { Action } from 'stores/Workflows/Action'
import { ConfigureMoveFileAutomationModal } from 'components/ConfigureMoveFileAutomationModal/ConfigureMoveFileAutomationModal'
import { IconButton } from 'components/IconButton/IconButton'
import { ConfigureCreateAccessesAutomationModal } from 'components/ConfigureCreateAccessesAutomationModal/ConfigureCreateAccessesAutomationModal'
import { useModals } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  state: State
  onSelectionChange: (spec?: ActionSpec) => void
}

const iconForValue = (value?: ActionSpec) => {
  if (!value) {
    return undefined
  }

  switch (value.type) {
    case 'reviews.create':
      return Review as FunctionComponent<React.PropsWithChildren<SVGProps<SVGSVGElement>>>
    case 'approvals.create':
      return Approval as FunctionComponent<React.PropsWithChildren<SVGProps<SVGSVGElement>>>
    case 'read_receipts.create':
      return ReadReceipt as FunctionComponent<React.PropsWithChildren<SVGProps<SVGSVGElement>>>
    case 'files.export':
      return ExportIcon
    case 'files.move':
      return MoveIcon
    case 'accesses.create':
      return ShareIcon
    case 'files.copy':
      return DocIcon
    default:
      return undefined
  }
}

const REVIEW_TYPES_MAP: Record<string, ReviewType> = {
  'reviews.create': ReviewType.FEEDBACK,
  'approvals.create': ReviewType.APPROVAL,
  'read_receipts.create': ReviewType.READ_RECEIPTS,
}

export const useConfigPrompt = (state: State) => {
  const { isMobile } = useResponsiveDesign()
  const { openModal, closeModal } = useModals()

  return (type: ActionType) => {
    const props = type === state.entryAction?.type ? state.entryAction.config : {}

    switch (type) {
      case 'reviews.create':
      case 'approvals.create':
      case 'read_receipts.create':
        const toStatus =
          type === state.entryAction?.type
            ? state.childStates[0]?.propertyValue
            : { value: CHILD_STATE_NAMES[type], meta: { color: '#EDEEF0' } }
        return new Promise((resolve) => {
          openModal(
            ConfigureReviewAutomationModal,
            {
              ...props,
              reviewType: REVIEW_TYPES_MAP[type] as ReviewType,
              fromStatus: state.propertyValue,
              toStatus,
              onConfirm: resolve,
              onCancel: closeModal,
              onClose: closeModal,
            },
            { newStack: isMobile },
          )
        })
      case 'files.move':
        return new Promise((resolve) => {
          openModal(
            ConfigureMoveFileAutomationModal,
            {
              ...props,
              workflowFolderKey: state.workflow.folder.key,
              onConfirm: resolve,
              onCancel: closeModal,
              onClose: closeModal,
            },
            { newStack: isMobile },
          )
        })
      case 'accesses.create':
        return new Promise((resolve) => {
          openModal(
            ConfigureCreateAccessesAutomationModal,
            {
              ...props,
              onConfirm: resolve,
              onCancel: closeModal,
              onClose: closeModal,
            },
            { newStack: isMobile },
          )
        })
      default:
        return Promise.resolve(null)
    }
  }
}

const ConfigurableItemIcon = observer(({ state, actionType }: { state: State; actionType: ActionType }) => {
  const Icon = state.entryAction?.type === actionType ? SettingsIcon : ChevronRight
  return <Icon aria-hidden className={Styles.actionPickerItemSettingsIcon} />
})

const ConfigureButton = observer(({ state, onPress }: { state: State; onPress: () => void }) => {
  if (state.childStates.length > 0) {
    return (
      <Button variant="tertiaryGreen" size="lg" frontIcon={SettingsIcon} onPress={onPress}>
        Configure
      </Button>
    )
  }

  return <IconButton variant="tertiaryGreen" size="lg" icon={SettingsIcon} onPress={onPress} title="Configure" />
})

export const ActionPicker = observer(({ state, onSelectionChange }: Props) => {
  const configPrompt = useConfigPrompt(state)

  const handleSelectionChange = async (key: Key) => {
    const type = key as ActionType | 'none'
    if (type === 'none') {
      onSelectionChange(undefined)
      return
    }

    if (Action.CONFIGURABLE_TYPES.includes(type)) {
      const config = await configPrompt(type)
      if (config) {
        onSelectionChange({ type, config } as ActionSpec)
      }
      return
    }

    onSelectionChange({ type, config: {} } as ActionSpec)
  }

  return (
    <>
      <Select
        size="lg"
        placement="bottom-end"
        aria-label="Automation"
        placeholder="No Automation"
        selectedKey={state.entryAction?.type || 'none'}
        onSelectionChange={handleSelectionChange}
        renderSelectValue={(item) => item?.props.textValue}
        icon={iconForValue(state.entryAction?.spec)}
        popoverClass={Styles.actionPickerPopover}
      >
        <Section>
          <Item key="none" textValue="No Automation">
            No Automation
          </Item>
        </Section>

        <Section key="requests" title="Requests">
          <Item textValue="Request Review" key="reviews.create">
            <Review aria-hidden />
            Request Review <ConfigurableItemIcon state={state} actionType="reviews.create" />
          </Item>
          <Item textValue="Request Approval" key="approvals.create">
            <Approval aria-hidden />
            Request Approval <ConfigurableItemIcon state={state} actionType="approvals.create" />
          </Item>
          <Item textValue="Request Read Receipts" key="read_receipts.create">
            <ReadReceipt aria-hidden />
            Request Read Receipts <ConfigurableItemIcon state={state} actionType="read_receipts.create" />
          </Item>
        </Section>

        <Section key="other" title="Other">
          <Item textValue="Make a Copy" key="files.copy">
            <DocIcon aria-hidden />
            Make a Copy
          </Item>
          <Item textValue="Share with New People" key="accesses.create">
            <ShareIcon aria-hidden />
            Share with New People <ConfigurableItemIcon state={state} actionType="accesses.create" />
          </Item>
          <Item textValue="Export" key="files.export">
            <ExportIcon aria-hidden />
            Export
          </Item>
          <Item textValue="Move to Project" key="files.move">
            <MoveIcon aria-hidden />
            Move to Project
          </Item>
        </Section>
      </Select>
      {state.entryAction?.isConfigurable && (
        <ConfigureButton
          state={state}
          onPress={() => {
            if (!state.entryAction) {
              return
            }
            handleSelectionChange(state.entryAction.type)
          }}
        />
      )}
    </>
  )
})
